import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { validateEmail } from "@/lib/email";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailErr = validateEmail(email);
    if (emailErr) { setEmailError(emailErr); return; }
    setLoading(true);
    try {
      const data = await login(email, password);
      const roles = data.roles || [];
      const admin = roles.some((r) => r.role === "GLOBAL_ADMIN" || r.role === "CHAPTER_ADMIN");
      navigate(admin ? "/admin/members" : data.member ? "/home" : "/me", { replace: true });
    } catch (err) {
      toast.error(err.message || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-lv-bg flex items-center justify-center px-6" data-testid="signin-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <Link to="/" className="font-serif text-2xl text-cream tracking-[0.25em]" data-testid="signin-logo">LV PASS</Link>
          <p className="text-xs tracking-[0.3em] uppercase text-lv-muted mt-4">Sign In</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-xs tracking-[0.2em] uppercase text-lv-muted mb-2" htmlFor="signin-email">Email</label>
            <input id="signin-email" type="text" inputMode="email" autoComplete="email" value={email} onChange={(e) => { setEmail(e.target.value); setEmailError(null); }} className={`lv-input${emailError ? " border-red-500/70" : ""}`} placeholder="your@email.com" required data-testid="signin-email" />
            {emailError && <p className="text-xs text-red-400 mt-1.5 leading-relaxed" data-testid="signin-email-error">{emailError}</p>}
          </div>
          <div>
            <label className="block text-xs tracking-[0.2em] uppercase text-lv-muted mb-2" htmlFor="signin-pw">Password</label>
            <div className="relative">
              <input id="signin-pw" type={showPw ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="lv-input pr-12" placeholder="Enter password" required data-testid="signin-password" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-lv-muted hover:text-cream transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid="signin-toggle-pw" aria-label="Toggle password visibility">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} className="lv-btn-primary w-full min-h-[48px]" data-testid="signin-submit">
            {loading ? "Signing in\u2026" : "Sign In"}
          </button>
        </form>
        <div className="mt-8 text-center">
          <Link to="/auth/reset" className="text-sm text-lv-muted hover:text-bronze transition-colors tracking-wide min-h-[44px] inline-flex items-center" data-testid="signin-reset-link">
            Forgot password?
          </Link>
        </div>
        <div className="mt-10 text-center">
          <Link to="/" className="text-xs tracking-[0.2em] uppercase text-lv-muted/50 hover:text-lv-muted transition-colors">Back to home</Link>
        </div>
      </div>
    </div>
  );
}
